import { InformationCircleIcon } from '@heroicons/react/24/solid';
import { FormFieldError, Tooltip } from '@kira/checkpoint-shared-frontend';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { FieldError } from 'react-hook-form';
import TextareaAutosize from 'react-textarea-autosize';
import { twMerge } from 'tailwind-merge';

interface ITextAreaProps {
  defaultValue?: string;
  errors?: FieldError;
  id: string;
  inputClassName?: string;
  isDisabled?: boolean;
  label?: string;
  placeholder?: string;
  tooltipText?: string;
  value: string;
  wrapperClassName?: string;
  autoFocus?: boolean;
  onChange?: (event: React.FocusEvent<HTMLTextAreaElement, Element>) => void;
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement, Element>) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement, Element>) => void;
  dataTestId?: string;
  errorDataTestId?: string;
}

const TextArea = React.forwardRef<HTMLTextAreaElement, ITextAreaProps>(
  (
    {
      defaultValue,
      errors,
      id,
      inputClassName,
      isDisabled,
      label,
      placeholder,
      tooltipText,
      value,
      wrapperClassName,
      autoFocus,
      onFocus,
      onBlur,
      dataTestId,
      errorDataTestId,
      ...props
    },
    ref
  ) => {
    const [isFocus, setIsFocus] = useState(false);
    const handleChange = (event?: ChangeEvent<HTMLTextAreaElement>) => {
      const textarea = document.getElementById(id) as HTMLInputElement;
      if (textarea) {
        textarea.value = event?.target?.value || value || defaultValue || '';
      }
    };

    useEffect(() => {
      handleChange();
    }, [value]);

    const handleOnFocus = (
      event: React.FocusEvent<HTMLTextAreaElement, Element>
    ) => {
      setIsFocus(true);
      if (onFocus) {
        onFocus(event);
      }
    };

    const handleOnBlur = (
      event: React.FocusEvent<HTMLTextAreaElement, Element>
    ) => {
      setIsFocus(false);
      if (onBlur) {
        onBlur(event);
      }
    };

    const textareaClassName = twMerge(
      'border-b-1',
      'border-kira-slate-200',
      'border-t-0',
      'border-x-0',
      'disabled:cursor-not-allowed',
      'disabled:opacity-75',
      'focus:ring-0',
      'p-1',
      'prose-body-md-reg',
      'text-kira-slate-900',
      'w-full',
      'resize-none',
      'break-words',
      'overflow-hidden',
      'bg-transparent',
      'z-10',
      `${
        errors
          ? 'border-kira-red-800 focus:border-kira-red-800'
          : 'focus:border-kira-blue-800'
      }`
    );

    const isFocusOrHasValue = isFocus || value || defaultValue;
    const labelClassNames = twMerge(
      'absolute',
      'transition-all',
      'w-full',
      'flex',
      'z-0',
      'duration-300',
      `${
        isFocusOrHasValue
          ? 'top-1 prose-title-input'
          : 'top-2 -right-1 prose-body-md-reg'
      }`,
      `${isFocus ? 'text-kira-blue-800' : 'text-kira-slate-700'}`,
      `${errors && 'text-kira-red-800'}`,
      `${inputClassName ?? ''}`
    );
    const wrapperClassNames = twMerge(
      'relative',
      'flex-1',
      `${label && isFocusOrHasValue ? 'pt-4' : 'mt-4'}`,
      `${!label ? 'p-0 mt-0' : ''}`,
      `${wrapperClassName ?? ''}`
    );

    return (
      <div className={wrapperClassNames}>
        <label htmlFor={id} className={labelClassNames}>
          {label}
          {tooltipText && (
            <Tooltip
              textContent={tooltipText}
              tooltipClassNames="z-50"
              textClassNames="text-left whitespace-pre-line"
            >
              <InformationCircleIcon className="ml-1 inline-block h-3 w-3 text-kira-slate-700" />
            </Tooltip>
          )}
        </label>
        <TextareaAutosize
          className={textareaClassName}
          data-testid={dataTestId}
          defaultValue={defaultValue || value}
          disabled={isDisabled}
          id={id}
          placeholder={placeholder}
          onChange={handleChange}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          ref={ref}
          autoFocus={autoFocus}
          {...props}
        />
        <FormFieldError
          isVisible={!!errors}
          message={errors?.message ?? ''}
          dataTestId={errorDataTestId}
        />
      </div>
    );
  }
);

export default TextArea;
